import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TaskCard from "@/components/TaskCard";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";
import { TeamDashboard } from "@/components/TeamDashboard";
import { OwnerDashboard } from "@/components/OwnerDashboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatWIB } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ClipboardList, AlertTriangle, CheckCircle2, Clock, Inbox, CalendarDays, TrendingUp, Users,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    indigo: "from-indigo-500 to-purple-500", rose: "from-rose-500 to-orange-500",
    sky: "from-sky-500 to-cyan-500", emerald: "from-emerald-500 to-teal-500", amber: "from-amber-500 to-orange-500",
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm" data-testid={testid}>
      <div className={cn("inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white mb-3", tones[tone])}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-extrabold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children, count }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-slate-400" />
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</h3>
        {count != null && <span className="text-xs font-bold text-slate-400">({count})</span>}
      </div>
      {children}
    </div>
  );
}

function PersonalView({ onOpen }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/dashboard/personal").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-400 py-10 text-center">Memuat dashboard...</div>;
  const s = data.summary;
  const empty = (msg) => <p className="text-sm text-slate-400 italic">{msg}</p>;
  return (
    <div className="space-y-8 zoa-fade-up">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={ClipboardList} label="Tugas Aktif" value={s.total_active} tone="indigo" testid="stat-active" />
        <StatCard icon={AlertTriangle} label="Terlambat" value={s.overdue} tone="rose" testid="stat-overdue" />
        <StatCard icon={Clock} label="Jatuh Tempo Hari Ini" value={s.today} tone="amber" testid="stat-today" />
        <StatCard icon={CalendarDays} label="Mendekati Deadline" value={s.approaching} tone="sky" testid="stat-approaching" />
        <StatCard icon={CheckCircle2} label="Menunggu Verifikasi" value={s.waiting_verification} tone="sky" testid="stat-waiting" />
        <StatCard icon={Inbox} label="Persetujuan Saya" value={s.approvals_pending} tone="emerald" testid="stat-approvals" />
      </div>

      {data.overdue_tasks.length > 0 && (
        <Section title="Terlambat" icon={AlertTriangle} count={data.overdue_tasks.length}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.overdue_tasks.map((t) => <TaskCard key={t.id} task={t} onClick={() => onOpen(t.id)} />)}</div>
        </Section>
      )}

      <Section title="Tugas Hari Ini" icon={Clock} count={data.today_tasks.length}>
        {data.today_tasks.length === 0 ? empty("Tidak ada tugas jatuh tempo hari ini. 🎉") :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.today_tasks.map((t) => <TaskCard key={t.id} task={t} onClick={() => onOpen(t.id)} />)}</div>}
      </Section>

      <Section title="Mendekati Deadline (48 jam)" icon={CalendarDays} count={data.approaching_tasks.length}>
        {data.approaching_tasks.length === 0 ? empty("Belum ada yang mendekati deadline.") :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.approaching_tasks.map((t) => <TaskCard key={t.id} task={t} onClick={() => onOpen(t.id)} />)}</div>}
      </Section>

      {data.waiting_verification.length > 0 && (
        <Section title="Menunggu Verifikasi" icon={CheckCircle2} count={data.waiting_verification.length}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.waiting_verification.map((t) => <TaskCard key={t.id} task={t} onClick={() => onOpen(t.id)} />)}</div>
        </Section>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = ["atasan", "owner", "kepala_divisi", "super_admin"].includes(user?.role);
  const isOwner = ["owner", "super_admin", "auditor"].includes(user?.role);
  const [openId, setOpenId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const open = (id) => { setOpenId(id); setDrawerOpen(true); };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm">{formatWIB(new Date().toISOString())}</p>
      </div>
      {isManager ? (
        <Tabs defaultValue={isOwner ? "organisasi" : "saya"}>
          <TabsList className="mb-6">
            <TabsTrigger value="saya" data-testid="dashboard-tab-saya">Saya</TabsTrigger>
            <TabsTrigger value="tim" data-testid="dashboard-tab-tim">Tim</TabsTrigger>
            {isOwner && <TabsTrigger value="organisasi" data-testid="dashboard-tab-organisasi">Organisasi</TabsTrigger>}
          </TabsList>
          <TabsContent value="saya"><PersonalView key={`p${reloadKey}`} onOpen={open} /></TabsContent>
          <TabsContent value="tim"><TeamDashboard key={`t${reloadKey}`} onOpen={open} /></TabsContent>
          {isOwner && <TabsContent value="organisasi"><OwnerDashboard key={`o${reloadKey}`} /></TabsContent>}
        </Tabs>
      ) : <PersonalView key={`p${reloadKey}`} onOpen={open} />}

      <TaskDetailDrawer taskId={openId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
